/**
 * Box Component
 *
 * A simple container with optional dimensions and styling.
 * Component follows Smoothie UI architecture.
 *
 * @module @smoothie-framework/ui/components
 */
import { createNodeId } from '../../render/node-id';
import { normalizeChildrenToNodes } from '../../internal/children';
import { resolveCanonicalUtilityProps, } from '../../authoring/utility-props';
import { composeAsChildNode } from '../../authoring/as-child';
import { buildCommonUIProps, } from '../../authoring/common-props';
/**
 * Box component - a simple container with optional dimensions
 *
 * @example
 * ```tsx
 * <Box width="100%" padding={4} background="neutral.100">
 *   <Text>Content</Text>
 * </Box>
 *
 * <Box maxWidth="md" margin="auto">
 *   <Heading>Centered Content</Heading>
 * </Box>
 * ```
 */
export function Box(props) {
    const { id, as, asChild, style, role, ariaLabel, ariaLabelledBy, 'aria-labelledby': ariaLabelledByAttribute, ariaHidden, 'aria-hidden': ariaHiddenAttribute, tabIndex, ariaDescribedBy, className, children, 'data-ui': dataUi, ...utilityPropsInput } = props;
    const baseClass = 'smth-box';
    const classes = [baseClass];
    if (className) {
        classes.push(className);
    }
    const boxProps = {
        className: classes.join(' '),
        ...(typeof as === 'string' && as.length > 0 && { 'data-as': as }),
        ...resolveCanonicalUtilityProps(utilityPropsInput),
        ...buildCommonUIProps({
            id,
            role,
            ariaLabel,
            ariaLabelledBy: ariaLabelledBy !== undefined ? ariaLabelledBy : ariaLabelledByAttribute,
            ariaHidden: ariaHidden ?? ariaHiddenAttribute,
            tabIndex,
            ariaDescribedBy,
            style,
            dataUi,
            defaultDataUi: 'Box',
        }),
    };
    if (asChild) {
        return composeAsChildNode({
            componentType: 'Box',
            props: boxProps,
            children,
            explicitDataUi: dataUi,
            semanticTagOverride: as,
        });
    }
    const childNodes = normalizeChildren(children);
    return {
        id: createNodeId(),
        type: 'Box',
        props: boxProps,
        children: childNodes,
    };
}
function normalizeChildren(children) {
    return normalizeChildrenToNodes(children);
}
/**
 * Default props for Box component
 */
export const defaultBoxProps = {};
//# sourceMappingURL=Box.js.map